import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Key, Eye, EyeOff, Lock, Plus, Folder, Edit, Trash2, Copy } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { passwordManagerService } from '../lib/passwordManager'

const PasswordManager = () => {
  const { user } = useAuth()
  const [isUnlocked, setIsUnlocked] = useState(false)
  const [isFirstTime, setIsFirstTime] = useState(false)
  const [masterPassword, setMasterPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Password manager state
  const [categories, setCategories] = useState([])
  const [passwords, setPasswords] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [showAddPassword, setShowAddPassword] = useState(false)
  const [showEditPassword, setShowEditPassword] = useState(false)
  const [editingPassword, setEditingPassword] = useState(null)
  const [newCategoryName, setNewCategoryName] = useState('')
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    notes: '',
    categoryId: null
  })
  const [showPasswordInForm, setShowPasswordInForm] = useState(false)

  // Check if user has master password set up
  useEffect(() => {
    const checkMasterPasswordSetup = async () => {
      if (!user) return
      
      setLoading(true)
      try {
        const hasPassword = await passwordManagerService.hasMasterPassword(user.id)
        setIsFirstTime(!hasPassword)
      } catch (error) {
        console.error('Error checking master password setup:', error)
        setError('Failed to check password manager setup')
      } finally {
        setLoading(false)
      }
    }

    checkMasterPasswordSetup()
  }, [user])

  // Load data after successful unlock
  const loadPasswordManagerData = async () => {
    try {
      const [loadedCategories, loadedPasswords] = await Promise.all([
        passwordManagerService.loadCategories(user.id),
        passwordManagerService.loadPasswords(user.id)
      ])
      
      setCategories(loadedCategories)
      setPasswords(loadedPasswords)
    } catch (error) {
      console.error('Error loading password manager data:', error)
      setError('Failed to load your passwords')
    }
  }

  const handleSetupMasterPassword = async (e) => {
    e.preventDefault()
    if (!masterPassword.trim()) {
      setError('Please enter a master password')
      return
    }
    if (masterPassword.length < 8) {
      setError('Master password must be at least 8 characters')
      return
    }
    if (masterPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')
    
    try {
      await passwordManagerService.setupMasterPassword(user.id, masterPassword)
      setIsUnlocked(true)
      setIsFirstTime(false)
      setMasterPassword('')
      setConfirmPassword('')
      await loadPasswordManagerData()
    } catch (err) {
      setError('Failed to set up master password')
    } finally {
      setLoading(false)
    }
  }

  const handleUnlock = async (e) => {
    e.preventDefault()
    if (!masterPassword.trim()) {
      setError('Please enter your master password')
      return
    }

    setLoading(true)
    setError('')
    
    try {
      const isValid = await passwordManagerService.verifyMasterPassword(user.id, masterPassword)
      
      if (isValid) {
        setIsUnlocked(true)
        setMasterPassword('')
        await loadPasswordManagerData()
      } else {
        setError('Invalid master password')
      }
    } catch (err) {
      setError('Failed to verify master password')
    } finally {
      setLoading(false)
    }
  }

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return
    
    try {
      const newCategory = await passwordManagerService.saveCategory(user.id, {
        name: newCategoryName.trim()
      })
      
      setCategories([...categories, newCategory])
      setNewCategoryName('')
      setShowAddCategory(false)
    } catch (error) {
      setError('Failed to add category')
    }
  }

  const handleDeleteCategory = async (categoryId) => {
    try {
      await passwordManagerService.deleteCategory(user.id, categoryId)
      setCategories(categories.filter(cat => cat.id !== categoryId))
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null)
      }
    } catch (error) {
      setError('Failed to delete category')
    }
  }

  const getPasswordsForCategory = (categoryId) => {
    return passwords.filter(pwd => pwd.categoryId === categoryId)
  }

  const getAllUncategorizedPasswords = () => {
    return passwords.filter(pwd => !pwd.categoryId)
  }

  const handleAddPassword = async () => {
    if (!passwordForm.title.trim() || !passwordForm.password.trim()) {
      return
    }

    try {
      const newPassword = await passwordManagerService.savePassword(user.id, {
        title: passwordForm.title.trim(),
        username: passwordForm.username.trim(),
        password: passwordForm.password,
        url: passwordForm.url.trim(),
        notes: passwordForm.notes.trim(),
        categoryId: passwordForm.categoryId
      })

      setPasswords([...passwords, newPassword])
      resetPasswordForm()
      setShowAddPassword(false)
    } catch (error) {
      setError('Failed to save password')
    }
  }

  const resetPasswordForm = () => {
    setPasswordForm({
      title: '',
      username: '',
      password: '',
      url: '',
      notes: '',
      categoryId: null
    })
    setShowPasswordInForm(false)
  }

  const handleFormChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleEditPassword = (password) => {
    setEditingPassword(password)
    setPasswordForm({
      title: password.title,
      username: password.username,
      password: password.password,
      url: password.url,
      notes: password.notes,
      categoryId: password.categoryId
    })
    setShowEditPassword(true)
  }

  const handleUpdatePassword = async () => {
    if (!passwordForm.title.trim() || !passwordForm.password.trim()) {
      return
    }

    try {
      // Delete old password and create new one (since we can't update encrypted data easily)
      await passwordManagerService.deletePassword(user.id, editingPassword.id)
      
      const updatedPassword = await passwordManagerService.savePassword(user.id, {
        title: passwordForm.title.trim(),
        username: passwordForm.username.trim(),
        password: passwordForm.password,
        url: passwordForm.url.trim(),
        notes: passwordForm.notes.trim(),
        categoryId: passwordForm.categoryId
      })

      setPasswords(passwords.map(p => 
        p.id === editingPassword.id ? updatedPassword : p
      ))
      
      resetPasswordForm()
      setShowEditPassword(false)
      setEditingPassword(null)
    } catch (error) {
      setError('Failed to update password')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading password manager...</p>
        </div>
      </div>
    )
  }

  if (!isUnlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <motion.div
              animate={{ 
                rotate: [0, 2, -2, 0],
                scale: [1, 1.02, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-block mb-6 p-4 bg-white rounded-2xl shadow-sm border border-gray-200"
            >
              <Lock className="w-16 h-16 text-gray-700" />
            </motion.div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Password Manager
            </h2>
            <p className="text-gray-600">
              {isFirstTime 
                ? 'Set up your master password to secure your vault'
                : 'Enter your master password to access your saved passwords'
              }
            </p>
          </div>

          <form onSubmit={isFirstTime ? handleSetupMasterPassword : handleUnlock} className="card space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  className="input-field pr-12"
                  placeholder={isFirstTime ? "Create your master password (min 8 characters)" : "Enter your master password"}
                  disabled={loading}
                  minLength={isFirstTime ? 8 : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password - only for first time setup */}
            {isFirstTime && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Master Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="Confirm your master password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-600 text-sm"
              >
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {loading 
                ? (isFirstTime ? 'Setting up...' : 'Unlocking...') 
                : (isFirstTime ? 'Create Password Manager' : 'Unlock Password Manager')
              }
            </button>
          </form>
        </motion.div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Password Manager</h1>
        <button
          onClick={() => setIsUnlocked(false)}
          className="btn-secondary"
        >
          <Lock className="w-4 h-4 mr-2" />
          Lock
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
              <button
                onClick={() => setShowAddCategory(true)}
                className="text-gray-400 hover:text-gray-600 p-1"
                title="Add Category"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Add Category Form */}
            {showAddCategory && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-4 p-3 bg-gray-50 rounded-lg"
              >
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name (e.g., Google, Banking)"
                  className="input-field mb-2"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddCategory}
                    className="btn-primary text-sm py-1 px-3"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddCategory(false)
                      setNewCategoryName('')
                    }}
                    className="btn-secondary text-sm py-1 px-3"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            {/* All Passwords */}
            <button
              onClick={() => setSelectedCategory(null)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg mb-2 transition-colors ${
                !selectedCategory 
                  ? 'bg-gray-100 text-gray-900' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Key className="w-5 h-5" />
              <span>All Passwords</span>
              <span className="ml-auto text-sm text-gray-500">
                {getAllUncategorizedPasswords().length}
              </span>
            </button>

            {/* Category List */}
            <div className="space-y-1">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    selectedCategory?.id === category.id 
                      ? 'bg-gray-100 text-gray-900' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => setSelectedCategory(category)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <Folder className="w-5 h-5" />
                    <span>{category.name}</span>
                    <span className="ml-auto text-sm text-gray-500">
                      {getPasswordsForCategory(category.id).length}
                    </span>
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-gray-400 hover:text-red-500 p-1"
                    title="Delete Category"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {categories.length === 0 && !showAddCategory && (
              <div className="text-center py-6 text-gray-500">
                <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No categories yet</p>
                <p className="text-xs">Click + to add your first category</p>
              </div>
            )}
          </div>
        </div>

        {/* Password List */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedCategory ? selectedCategory.name : 'All Passwords'}
              </h2>
              <button
                onClick={() => setShowAddPassword(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Password
              </button>
            </div>

            {/* Password List Content */}
            <div className="space-y-3">
              {(() => {
                const displayPasswords = selectedCategory 
                  ? getPasswordsForCategory(selectedCategory.id)
                  : getAllUncategorizedPasswords()
                
                if (displayPasswords.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {selectedCategory ? `No passwords in ${selectedCategory.name}` : 'No passwords yet'}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {selectedCategory 
                          ? `Add your first password to the ${selectedCategory.name} category`
                          : 'Add your first password to get started'
                        }
                      </p>
                      <button
                        onClick={() => setShowAddPassword(true)}
                        className="btn-primary"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Password
                      </button>
                    </div>
                  )
                }

                return displayPasswords.map((password) => (
                  <motion.div
                    key={password.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="file-item p-4 flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <Key className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <h3 className="font-medium text-gray-900 truncate">
                          {password.title}
                        </h3>
                        {password.url && (
                          <a
                            href={password.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm"
                            title="Open website"
                          >
                            🔗
                          </a>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        {password.username && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Username:</span>
                            <span className="font-mono">{password.username}</span>
                            <button
                              onClick={() => navigator.clipboard.writeText(password.username)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy username"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Password:</span>
                          <span className="font-mono">••••••••</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(password.password)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy password"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {password.notes && (
                          <div className="mt-2">
                            <span className="text-gray-500">Notes:</span>
                            <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                              {password.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditPassword(password)}
                        className="text-gray-400 hover:text-gray-600 p-2"
                        title="Edit password"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await passwordManagerService.deletePassword(user.id, password.id)
                            setPasswords(passwords.filter(p => p.id !== password.id))
                          } catch (error) {
                            setError('Failed to delete password')
                          }
                        }}
                        className="text-gray-400 hover:text-red-500 p-2"
                        title="Delete password"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Add Password Modal */}
      {showAddPassword && (
        <div className="modal-overlay">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-lg w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add New Password</h3>
              <button
                onClick={() => {
                  setShowAddPassword(false)
                  resetPasswordForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAddPassword(); }} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={passwordForm.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="e.g., Gmail Work Account, Facebook, Bank Login"
                  className="input-field"
                  required
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={passwordForm.categoryId || ''}
                  onChange={(e) => handleFormChange('categoryId', e.target.value || null)}
                  className="input-field"
                >
                  <option value="">No Category (All Passwords)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Username/Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username/Email
                </label>
                <input
                  type="text"
                  value={passwordForm.username}
                  onChange={(e) => handleFormChange('username', e.target.value)}
                  placeholder="e.g., john@example.com, username123"
                  className="input-field"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPasswordInForm ? 'text' : 'password'}
                    value={passwordForm.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    placeholder="Enter password"
                    className="input-field pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordInForm(!showPasswordInForm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordInForm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={passwordForm.url}
                  onChange={(e) => handleFormChange('url', e.target.value)}
                  placeholder="e.g., https://gmail.com, https://facebook.com"
                  className="input-field"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={passwordForm.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes, security questions, etc."
                  className="input-field resize-none"
                  rows={3}
                />
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!passwordForm.title.trim() || !passwordForm.password.trim()}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Save Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPassword(false)
                    resetPasswordForm()
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Password Modal */}
      {showEditPassword && editingPassword && (
        <div className="modal-overlay">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-lg w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Edit Password</h3>
              <button
                onClick={() => {
                  setShowEditPassword(false)
                  setEditingPassword(null)
                  resetPasswordForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={passwordForm.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="e.g., Gmail Work Account, Facebook, Bank Login"
                  className="input-field"
                  required
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={passwordForm.categoryId || ''}
                  onChange={(e) => handleFormChange('categoryId', e.target.value || null)}
                  className="input-field"
                >
                  <option value="">No Category (All Passwords)</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Username/Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username/Email
                </label>
                <input
                  type="text"
                  value={passwordForm.username}
                  onChange={(e) => handleFormChange('username', e.target.value)}
                  placeholder="e.g., john@example.com, username123"
                  className="input-field"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPasswordInForm ? 'text' : 'password'}
                    value={passwordForm.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    placeholder="Enter password"
                    className="input-field pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordInForm(!showPasswordInForm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordInForm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={passwordForm.url}
                  onChange={(e) => handleFormChange('url', e.target.value)}
                  placeholder="e.g., https://gmail.com, https://facebook.com"
                  className="input-field"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={passwordForm.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes, security questions, etc."
                  className="input-field resize-none"
                  rows={3}
                />
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!passwordForm.title.trim() || !passwordForm.password.trim()}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditPassword(false)
                    setEditingPassword(null)
                    resetPasswordForm()
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  )
}

export default PasswordManager
